import { ComponentType } from 'react';
import { DocumentIcon, GridIcon, IconProps } from '~components/ui/icons';
import {
  ScheduleTimeBlockData,
  TimeBlockCategory,
  TimeBlockType,
} from '~graphql/__generated__/types';
import {
  ScheduleAppsIcon,
  ScheduleBookmarkIcon,
  ScheduleBrightnessIcon,
  ScheduleDisplaySourceIcon,
  ScheduleInputIcon,
  SchedulePlaylistIcon,
} from './icons';

type TimeBlockMeta = {
  label: string;
  /** Legend icon above the timeline. */
  icon: ComponentType<IconProps>;
  /** Icon drawn inside the block on the timeline. */
  blockIcon: ComponentType<IconProps>;
  /** Colour of the legend icon. */
  accent: string;
  /** Fill of the block on the timeline. */
  background: string;
  /** Fill of the icon badge in the time slot form. */
  badgeBackground: string;
  /** Colour of the icon drawn inside the block. */
  blockIconColor: string;
};

/**
 * Colours are taken from the schedules Figma file. Default Input source and Source use a
 * different glyph in the legend than on the timeline, which is why `blockIcon` is
 * tracked separately.
 */
export const TIME_BLOCK_META: Record<TimeBlockType, TimeBlockMeta> = {
  [TimeBlockType.DefaultInputSource]: {
    label: 'defaultInputSource',
    // Legend and slot share one glyph, as they do for brightness.
    icon: ScheduleInputIcon,
    blockIcon: ScheduleInputIcon,
    accent: 'red.200',
    background: 'red.100',
    badgeBackground: 'red.100',
    blockIconColor: 'blue.800',
  },
  [TimeBlockType.Apps]: {
    label: 'apps',
    icon: ScheduleAppsIcon,
    blockIcon: ScheduleAppsIcon,
    accent: 'blue.500',
    background: 'blue.100',
    badgeBackground: 'blue.100',
    blockIconColor: 'blue.800',
  },
  [TimeBlockType.Bookmark]: {
    label: 'bookmark',
    icon: ScheduleBookmarkIcon,
    blockIcon: ScheduleBookmarkIcon,
    accent: 'orange.200',
    background: 'orange.100',
    badgeBackground: 'orange.100',
    blockIconColor: 'blue.800',
  },
  [TimeBlockType.Source]: {
    label: 'source',
    // Legend and slot share one glyph, as they do for the default input source.
    icon: ScheduleDisplaySourceIcon,
    blockIcon: ScheduleDisplaySourceIcon,
    accent: 'green.200',
    background: 'green.100',
    badgeBackground: 'green.100',
    blockIconColor: 'blue.800',
  },
  [TimeBlockType.Playlist]: {
    label: 'playlist',
    icon: SchedulePlaylistIcon,
    blockIcon: SchedulePlaylistIcon,
    accent: 'purple.500',
    background: 'purple.100',
    // The design's --purple-100 sits between the theme's purple.100 and .200.
    badgeBackground: '#D8C6FF',
    blockIconColor: 'blue.800',
  },
  [TimeBlockType.Brightness]: {
    label: 'brightness',
    icon: ScheduleBrightnessIcon,
    blockIcon: ScheduleBrightnessIcon,
    accent: 'gray.600',
    background: 'gray.200',
    badgeBackground: 'gray.200',
    blockIconColor: 'gray.600',
  },
};

/** Colour of the text and icon inside a timeline block. */
export const TIME_BLOCK_CONTENT_COLOR = 'blue.800';

/** The "Schedule for" selector: which track the slot belongs to. */
export const CATEGORY_META: Record<
  TimeBlockCategory,
  { label: string; icon: ComponentType<IconProps>; accent: string; badgeBackground: string }
> = {
  [TimeBlockCategory.Content]: {
    label: 'content',
    icon: DocumentIcon,
    accent: 'blue.800',
    badgeBackground: 'gray.200',
  },
  [TimeBlockCategory.Settings]: {
    label: 'settings',
    icon: GridIcon,
    accent: 'blue.800',
    badgeBackground: 'gray.200',
  },
};

/**
 * Label for the type-specific payload field. Taken from the Figma "Schedules_Phase1 v2"
 * section, which labels the field per schedule type rather than reusing one caption.
 */
export const TIME_BLOCK_DATA_LABEL: Record<TimeBlockType, string> = {
  [TimeBlockType.Playlist]: 'selectPlaylist',
  [TimeBlockType.Apps]: 'selectApp',
  [TimeBlockType.Bookmark]: 'enterSite',
  [TimeBlockType.Source]: 'selectSource',
  [TimeBlockType.DefaultInputSource]: 'selectDefaultInputSource',
  [TimeBlockType.Brightness]: 'selectBrightnessLevel',
};

/** Which payload fields the design marks as required. */
export const TIME_BLOCK_DATA_REQUIRED: Record<TimeBlockType, boolean> = {
  [TimeBlockType.Playlist]: true,
  [TimeBlockType.Apps]: true,
  [TimeBlockType.Bookmark]: false,
  [TimeBlockType.Source]: true,
  [TimeBlockType.DefaultInputSource]: true,
  [TimeBlockType.Brightness]: false,
};

/**
 * The block's payload rendered for humans. Playlist titles are resolved by the
 * caller when it has them; the id is the fallback so a block is never blank.
 */
export function describeTimeBlockData(
  type: TimeBlockType,
  data: Pick<
    ScheduleTimeBlockData,
    'applicationId' | 'id' | 'mediaType' | 'source' | 'url' | 'value'
  >,
  resolvePlaylistTitle?: (playlistId: string) => string | undefined,
  resolveAppName?: (applicationId: string) => string | undefined,
): string {
  switch (type) {
    case TimeBlockType.Playlist:
      return (data.id && resolvePlaylistTitle?.(data.id)) || data.id || '';
    case TimeBlockType.Apps:
      return (
        (data.applicationId && resolveAppName?.(data.applicationId)) || data.applicationId || ''
      );
    case TimeBlockType.Bookmark:
      return data.url ?? '';
    case TimeBlockType.Source:
    case TimeBlockType.DefaultInputSource:
      return data.source ?? '';
    case TimeBlockType.Brightness:
      return data.value == null ? '' : `${data.value}%`;
    default:
      return '';
  }
}
